import {
  Activity,
  BarChart3,
  ChevronRight,
  Flame,
  Info,
  RefreshCw,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  EmptyState,
  PageLayout,
  SectionHeader,
  Tabs,
  Tooltip,
} from "../components";
import { RevenueIntelligenceSkeleton } from "../components/skeletons";
import { useTranslation } from "../contexts/LanguageContext";
import { showToastError } from "../shared/alertService";
import apiService from "../shared/apiService";
import { API_ENDPOINTS } from "../shared/constants";
import { cn } from "../utils/cn";

// ─── Types ────────────────────────────────────────────────────────────────────

type Temperature = "ice" | "cold" | "warm" | "hot" | "burn";

interface TopTopic {
  topic: string;
  interest_score: number;
  interaction_count: number;
}

interface Lead {
  lead_id: number;
  external_id: string | null;
  email: string | null;
  username: string | null;
  engagement_score: number;
  temperature: Temperature;
  days_since_last_interaction: number | null;
  top_topics: TopTopic[];
  seller_action: string;
}

interface HotLeadsResponse {
  total: number;
  min_score: number;
  leads: Lead[];
}

interface CampaignRow {
  campaign: string;
  unique_leads: number;
  total_touchpoints: number;
  avg_engagement_score: number;
  temperature_breakdown: Partial<Record<Temperature, number>>;
  top_channels: { channel: string; count: number }[];
  top_topics: { topic: string; count: number }[];
}

interface AttributionResponse {
  days: number;
  total_campaigns: number;
  campaigns: CampaignRow[];
}

interface MediumRow {
  medium: string;
  leads: number;
  touchpoints: number;
}

interface SummaryResponse {
  days: number;
  by_medium: MediumRow[];
  organic_posts: {
    campaign: string;
    platform: string;
    leads: number;
    touchpoints: number;
  }[];
  paid_campaigns: {
    source: string;
    campaign: string;
    leads: number;
    touchpoints: number;
  }[];
}

// ─── Config por temperatura ───────────────────────────────────────────────────
// Escala de temperatura (frio → quente) é o próprio conteúdo semântico aqui —
// preservada mesmo divergindo da paleta genérica de estado (erro/sucesso),
// já que aqui "vermelho" significa "lead mais prioritário", não "erro".

const TEMP_CFG: Record<
  Temperature,
  {
    label: string;
    emoji: string;
    border: string;
    text: string;
    iconBg: string;
    barBg: string;
    badgeBg: string;
  }
> = {
  ice: {
    label: "Gelado",
    emoji: "🧊",
    border: "border-blue-200",
    text: "text-blue-700",
    iconBg: "bg-blue-100",
    barBg: "bg-blue-400",
    badgeBg: "bg-blue-50 border-blue-100",
  },
  cold: {
    label: "Frio",
    emoji: "❄️",
    border: "border-indigo-200",
    text: "text-indigo-700",
    iconBg: "bg-indigo-100",
    barBg: "bg-indigo-400",
    badgeBg: "bg-indigo-50 border-indigo-100",
  },
  warm: {
    label: "Morno",
    emoji: "☀️",
    border: "border-amber-200",
    text: "text-amber-700",
    iconBg: "bg-amber-100",
    barBg: "bg-amber-400",
    badgeBg: "bg-amber-50 border-amber-100",
  },
  hot: {
    label: "Quente",
    emoji: "🌡️",
    border: "border-orange-200",
    text: "text-orange-700",
    iconBg: "bg-orange-100",
    barBg: "bg-orange-500",
    badgeBg: "bg-orange-50 border-orange-100",
  },
  burn: {
    label: "Ardendo",
    emoji: "🔥",
    border: "border-rose-200",
    text: "text-rose-700",
    iconBg: "bg-rose-100",
    barBg: "bg-rose-500",
    badgeBg: "bg-rose-50 border-rose-100",
  },
};

const TEMP_ORDER: Temperature[] = ["burn", "hot", "warm", "cold", "ice"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function riGet<T>(path: string): Promise<T> {
  return apiService.get(path) as Promise<T>;
}

function displayName(lead: Lead): string {
  return (
    lead.username ?? lead.email ?? lead.external_id ?? `Lead #${lead.lead_id}`
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TempBadge({ temp }: { temp: Temperature }) {
  const c = TEMP_CFG[temp] ?? TEMP_CFG.ice;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border",
        c.badgeBg,
        c.text,
      )}
    >
      {c.emoji} {c.label}
    </span>
  );
}

function ScoreBar({ score }: { score: number }) {
  const color =
    score >= 75
      ? "bg-rose-500"
      : score >= 55
        ? "bg-orange-500"
        : score >= 35
          ? "bg-amber-400"
          : score >= 15
            ? "bg-indigo-400"
            : "bg-slate-300";
  return (
    <div className="flex items-center gap-2 min-w-[90px]">
      <div className="flex-1 h-1.5 rounded-full bg-gray-100">
        <div
          className={cn("h-full rounded-full", color)}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className="text-sm font-bold tabular-nums text-gray-800">
        {score}
      </span>
    </div>
  );
}

// ─── Tabela genérica (moldura compartilhada por todas as tabelas da página) ───

function TableFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-purple-200 overflow-x-auto">
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

function TableHeadRow({ headers }: { headers: React.ReactNode[] }) {
  return (
    <thead className="bg-purple-50/40 border-b border-purple-200">
      <tr>
        {headers.map((h, i) => (
          <th
            key={i}
            className="px-4 py-2.5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap"
          >
            {h}
          </th>
        ))}
      </tr>
    </thead>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type TabId = "leads" | "campaigns" | "summary";

export default function RevenueIntelligence() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabId>("leads");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [hotLeads, setHotLeads] = useState<HotLeadsResponse | null>(null);
  const [attribution, setAttribution] = useState<AttributionResponse | null>(
    null,
  );
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [tempCounts, setTempCounts] = useState<Record<Temperature, number>>({
    ice: 0,
    cold: 0,
    warm: 0,
    hot: 0,
    burn: 0,
  });

  const load = useCallback(
    async (isRefresh: boolean) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        const [hl, attr, summ] = await Promise.all([
          riGet<HotLeadsResponse>(
            `${API_ENDPOINTS.REVENUE.HOT_LEADS}?min_score=0&limit=100`,
          ),
          riGet<AttributionResponse>(
            `${API_ENDPOINTS.REVENUE.CAMPAIGNS_ATTRIBUTION}?days=30`,
          ),
          riGet<SummaryResponse>(
            `${API_ENDPOINTS.REVENUE.CAMPAIGNS_SUMMARY}?days=30`,
          ),
        ]);
        setHotLeads(hl);
        setAttribution(attr);
        setSummary(summ);

        const counts: Record<Temperature, number> = {
          ice: 0,
          cold: 0,
          warm: 0,
          hot: 0,
          burn: 0,
        };
        for (const l of hl.leads) {
          const temp = l.temperature as Temperature;
          if (temp in counts) counts[temp]++;
        }
        setTempCounts(counts);
      } catch {
        showToastError(
          t("Não foi possível carregar os dados de Revenue Intelligence."),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalLeads = Object.values(tempCounts).reduce((a, b) => a + b, 0);

  const tabs = [
    {
      id: "leads",
      label: t("Leads Quentes"),
      icon: <Flame className="h-3.5 w-3.5" />,
    },
    {
      id: "campaigns",
      label: t("Atribuição por Campanha"),
      icon: <Target className="h-3.5 w-3.5" />,
    },
    {
      id: "summary",
      label: t("Orgânico vs. Pago"),
      icon: <BarChart3 className="h-3.5 w-3.5" />,
    },
  ];

  return (
    <PageLayout
      title={t("Inteligência de Receita")}
      subtitle={t(
        "Score de engajamento multi-touch, temperatura e atribuição de campanhas.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2! py-2!"
      loading={loading}
      skeleton={<RevenueIntelligenceSkeleton />}
    >
      <div className="w-full flex flex-col gap-2">
        {/* ===================================================================== */}
        {/* BANDA: Temperatura de Leads                                            */}
        {/* ===================================================================== */}
        <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
          <SectionHeader
            icon={<Activity className="h-5 w-5" />}
            title={t("Temperatura de Leads")}
            subtitle={t("Prioridade de contato por engajamento acumulado")}
            iconBgClass="bg-purple-600"
            action={
              <Button
                variant="outline"
                size="sm"
                icon={
                  <RefreshCw
                    className={cn("h-4 w-4", refreshing && "animate-spin")}
                  />
                }
                onClick={() => load(true)}
                disabled={refreshing}
                className="rounded-lg h-9! py-1! px-3! border-gray-200! text-gray-700! hover:bg-gray-50! shadow-xs text-sm font-semibold whitespace-nowrap"
              >
                {t("Atualizar")}
              </Button>
            }
          />

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {TEMP_ORDER.map((temp) => {
              const cfg = TEMP_CFG[temp];
              const count = tempCounts[temp];
              const pct =
                totalLeads > 0 ? Math.round((count / totalLeads) * 100) : 0;
              return (
                <div
                  key={temp}
                  className={cn(
                    "relative bg-white rounded-xl border p-3.5 shadow-xs flex flex-col justify-between min-h-[104px] overflow-hidden",
                    cfg.border,
                  )}
                >
                  <div
                    className={cn(
                      "absolute top-0 left-0 right-0 h-[3px]",
                      cfg.iconBg,
                    )}
                  />
                  <div className="flex items-start justify-between pt-0.5">
                    <div className="flex flex-col min-w-0 pr-1">
                      <p
                        className={cn(
                          "text-[10px] font-bold uppercase tracking-widest truncate",
                          cfg.text,
                        )}
                      >
                        {t(cfg.label)}
                      </p>
                      <h3 className="text-2xl font-black tracking-tight leading-none tabular-nums text-gray-900 mt-1">
                        {count}
                      </h3>
                    </div>
                    <div
                      className={cn(
                        "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-xl leading-none",
                        cfg.iconBg,
                      )}
                    >
                      {cfg.emoji}
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all duration-500",
                          cfg.barBg,
                        )}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-gray-400 tabular-nums font-semibold mt-1">
                      {t("{pct}% do total", { pct: String(pct) })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ===================================================================== */}
        {/* Abas — cada uma tem densidade/composição própria (tabela única vs.    */}
        {/* grade de cards + 2 tabelas), então cada aba vira seu próprio card,     */}
        {/* não uma grid fixa compartilhada.                                      */}
        {/* ===================================================================== */}
        <Tabs
          tabs={tabs}
          activeTab={tab}
          onTabChange={(id) => setTab(id as TabId)}
          variant="pills"
          size="sm"
          mobileLayout="grid"
        />

        {tab === "leads" && (
          <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
            <SectionHeader
              icon={<Flame className="h-5 w-5" />}
              title={t("Leads Quentes")}
              subtitle={t(
                "{count} leads rastreados via WhatsApp, comentários e site",
                { count: String(hotLeads?.leads.length ?? 0) },
              )}
              iconBgClass="bg-rose-500"
            />

            {hotLeads?.leads.length === 0 ? (
              <EmptyState
                icon={<Users className="h-10 w-10" />}
                title={t("Nenhum lead rastreado ainda")}
                description={t(
                  "Interações via WhatsApp, comentários sociais e site aparecerão aqui.",
                )}
              />
            ) : (
              hotLeads && (
                <TableFrame>
                  <TableHeadRow
                    headers={[
                      t("Lead"),
                      <span
                        key="score"
                        className="inline-flex items-center gap-1"
                      >
                        {t("Score")}
                        <Tooltip
                          content={t(
                            "Pontuação de 0 a 100 calculada a partir do engajamento multi-touch (WhatsApp, comentários e site) do lead.",
                          )}
                          position="top"
                        >
                          <Info className="h-3 w-3 text-gray-400 hover:text-purple-600 transition-colors cursor-help" />
                        </Tooltip>
                      </span>,
                      t("Temperatura"),
                      t("Interesse principal"),
                      t("Ação sugerida"),
                      t("Última interação"),
                    ]}
                  />
                  <tbody className="divide-y divide-purple-100 bg-white">
                    {hotLeads.leads.map((lead) => (
                      <tr
                        key={lead.lead_id}
                        className="hover:bg-purple-50/30 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-800 truncate max-w-[180px]">
                            {displayName(lead)}
                          </p>
                          {lead.email && lead.username && (
                            <p className="text-xs text-gray-400 truncate">
                              {lead.email}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <ScoreBar score={lead.engagement_score} />
                        </td>
                        <td className="px-4 py-3">
                          <TempBadge temp={lead.temperature} />
                        </td>
                        <td className="px-4 py-3">
                          {lead.top_topics[0] ? (
                            <span className="inline-block px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-xs font-medium border border-purple-100">
                              {lead.top_topics[0].topic}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">
                              {t("N/D")}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 whitespace-nowrap">
                            <ChevronRight className="h-3 w-3 shrink-0" />
                            {lead.seller_action}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                          {lead.days_since_last_interaction == null
                            ? t("N/D")
                            : lead.days_since_last_interaction < 1
                              ? t("Hoje")
                              : lead.days_since_last_interaction < 2
                                ? t("Ontem")
                                : t("{n}d atrás", {
                                    n: String(
                                      Math.round(
                                        lead.days_since_last_interaction,
                                      ),
                                    ),
                                  })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </TableFrame>
              )
            )}
          </div>
        )}

        {tab === "campaigns" && (
          <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
            <SectionHeader
              icon={<Target className="h-5 w-5" />}
              title={t("Atribuição por Campanha")}
              subtitle={t("{count} campanhas com leads atribuídos", {
                count: String(attribution?.campaigns.length ?? 0),
              })}
              iconBgClass="bg-purple-600"
            />

            {attribution?.campaigns.length === 0 ? (
              <EmptyState
                icon={<TrendingUp className="h-10 w-10" />}
                title={t("Nenhuma campanha rastreada ainda")}
                description={t(
                  "Posts publicados pelo SMM ganham UTMs automáticos. Cliques no site serão atribuídos.",
                )}
              />
            ) : (
              attribution && (
                <TableFrame>
                  <TableHeadRow
                    headers={[
                      t("Campanha"),
                      t("Leads únicos"),
                      t("Touchpoints"),
                      <span
                        key="score"
                        className="inline-flex items-center gap-1"
                      >
                        {t("Score médio")}
                        <Tooltip
                          content={t(
                            "Média do score de engajamento (0-100) de todos os leads atribuídos a esta campanha.",
                          )}
                          position="top"
                        >
                          <Info className="h-3 w-3 text-gray-400 hover:text-purple-600 transition-colors cursor-help" />
                        </Tooltip>
                      </span>,
                      t("Temperatura"),
                      t("Top canal"),
                      t("Top tópico"),
                    ]}
                  />
                  <tbody className="divide-y divide-purple-100 bg-white">
                    {attribution.campaigns.map((c) => {
                      const topTemp = TEMP_ORDER.find(
                        (temp) => (c.temperature_breakdown[temp] ?? 0) > 0,
                      );
                      return (
                        <tr
                          key={c.campaign}
                          className="hover:bg-purple-50/30 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <p
                              className="font-semibold text-gray-800 truncate max-w-[200px]"
                              title={c.campaign}
                            >
                              {c.campaign}
                            </p>
                          </td>
                          <td className="px-4 py-3 font-bold tabular-nums text-gray-800">
                            {c.unique_leads}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-gray-500">
                            {c.total_touchpoints}
                          </td>
                          <td className="px-4 py-3">
                            <ScoreBar score={c.avg_engagement_score} />
                          </td>
                          <td className="px-4 py-3">
                            {topTemp ? (
                              <TempBadge temp={topTemp} />
                            ) : (
                              <span className="text-gray-300 text-xs">
                                {t("N/D")}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 capitalize">
                            {c.top_channels[0]?.channel ?? t("N/D")}
                          </td>
                          <td className="px-4 py-3">
                            {c.top_topics[0] ? (
                              <span className="inline-block px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-xs font-medium border border-purple-100">
                                {c.top_topics[0].topic}
                              </span>
                            ) : (
                              <span className="text-gray-300 text-xs">
                                {t("N/D")}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </TableFrame>
              )
            )}
          </div>
        )}

        {tab === "summary" && (
          <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
            <SectionHeader
              icon={<BarChart3 className="h-5 w-5" />}
              title={t("Orgânico vs. Pago")}
              subtitle={t("Origem dos leads por canal de mídia")}
              iconBgClass="bg-blue-600"
            />

            {!summary ||
            (summary.organic_posts.length === 0 &&
              summary.paid_campaigns.length === 0) ? (
              <EmptyState
                icon={<BarChart3 className="h-10 w-10" />}
                title={t("Sem dados de campanha ainda")}
                description={t(
                  "Posts publicados pelo SMM terão UTMs automáticos. Cliques aparecerão aqui.",
                )}
              />
            ) : (
              <div className="flex flex-col gap-3">
                {/* Cards por medium — largura de acordo com a quantidade real de
                    mediums retornados pela API, não uma grid fixa */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {summary.by_medium.map((m) => {
                    const total = summary.by_medium.reduce(
                      (a, b) => a + b.leads,
                      0,
                    );
                    const pct =
                      total > 0 ? Math.round((m.leads / total) * 100) : 0;
                    const isOrganic = m.medium === "organic";
                    const isPaid = ["cpc", "paid", "ppc", "cpm"].includes(
                      m.medium,
                    );
                    const scheme = isOrganic
                      ? {
                          border: "border-emerald-200",
                          text: "text-emerald-700",
                          bar: "bg-emerald-500",
                        }
                      : isPaid
                        ? {
                            border: "border-blue-200",
                            text: "text-blue-700",
                            bar: "bg-blue-500",
                          }
                        : {
                            border: "border-gray-200",
                            text: "text-gray-600",
                            bar: "bg-gray-400",
                          };
                    return (
                      <div
                        key={m.medium}
                        className={cn(
                          "rounded-xl border bg-white p-3 flex flex-col gap-1.5",
                          scheme.border,
                        )}
                      >
                        <div className="flex justify-between items-center">
                          <span
                            className={cn(
                              "text-[10px] font-black uppercase tracking-widest truncate",
                              scheme.text,
                            )}
                          >
                            {m.medium || t("Direto")}
                          </span>
                          <span
                            className={cn(
                              "text-xs font-bold tabular-nums",
                              scheme.text,
                            )}
                          >
                            {pct}%
                          </span>
                        </div>
                        <p className="text-2xl font-black tabular-nums leading-none text-gray-900">
                          {m.leads}
                        </p>
                        <p className="text-xs text-gray-400">
                          {t("{count} touchpoints", {
                            count: String(m.touchpoints),
                          })}
                        </p>
                        <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className={cn("h-full rounded-full", scheme.bar)}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {summary.organic_posts.length > 0 && (
                  <div>
                    <h3 className="text-xs font-black uppercase tracking-widest text-emerald-700 mb-1.5 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                      {t("Posts Orgânicos — leads por publicação")}
                    </h3>
                    <TableFrame>
                      <TableHeadRow
                        headers={[
                          t("Campanha (post ID)"),
                          t("Plataforma"),
                          t("Leads"),
                          t("Touchpoints"),
                        ]}
                      />
                      <tbody className="divide-y divide-purple-100 bg-white">
                        {summary.organic_posts.slice(0, 15).map((p, i) => (
                          <tr
                            key={i}
                            className="hover:bg-emerald-50/30 transition-colors"
                          >
                            <td className="px-4 py-2.5 font-medium text-gray-700 truncate max-w-[220px]">
                              {p.campaign}
                            </td>
                            <td className="px-4 py-2.5 text-gray-500 capitalize text-xs">
                              {p.platform}
                            </td>
                            <td className="px-4 py-2.5 font-bold tabular-nums text-gray-800">
                              {p.leads}
                            </td>
                            <td className="px-4 py-2.5 tabular-nums text-gray-400">
                              {p.touchpoints}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </TableFrame>
                  </div>
                )}

                {summary.paid_campaigns.length > 0 && (
                  <div>
                    <h3 className="text-xs font-black uppercase tracking-widest text-blue-700 mb-1.5 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                      {t("Campanhas Pagas — leads rastreados por UTM")}
                    </h3>
                    <TableFrame>
                      <TableHeadRow
                        headers={[
                          t("Campanha"),
                          t("Fonte"),
                          t("Leads"),
                          t("Touchpoints"),
                        ]}
                      />
                      <tbody className="divide-y divide-purple-100 bg-white">
                        {summary.paid_campaigns.slice(0, 15).map((p, i) => (
                          <tr
                            key={i}
                            className="hover:bg-blue-50/30 transition-colors"
                          >
                            <td className="px-4 py-2.5 font-medium text-gray-700 truncate max-w-[220px]">
                              {p.campaign}
                            </td>
                            <td className="px-4 py-2.5 text-gray-500 capitalize text-xs">
                              {p.source}
                            </td>
                            <td className="px-4 py-2.5 font-bold tabular-nums text-gray-800">
                              {p.leads}
                            </td>
                            <td className="px-4 py-2.5 tabular-nums text-gray-400">
                              {p.touchpoints}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </TableFrame>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
